import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Float } from "@react-three/drei";
import { ArenaCanvas } from "../three/Arena";
import Avatar from "../three/Avatar";
import { ConfettiRain } from "../three/Confetti";
import { useGame, useGameEvent } from "../game/GameProvider";
import { infoToastStyle } from "../utils/utils";
import { teamOf, ARENA } from "../game/theme";
import { sfx } from "../game/sounds";

// Simple gold trophy built from primitives.
function Trophy({ position = [0, 0, 0] }) {
  return (
    <Float speed={2.4} rotationIntensity={0.5} floatIntensity={0.7}>
      <group position={position}>
        <mesh position={[0, 0.15, 0]}>
          <cylinderGeometry args={[0.28, 0.38, 0.3, 20]} />
          <meshStandardMaterial color={ARENA.gold} metalness={0.9} roughness={0.2} emissive={ARENA.gold} emissiveIntensity={0.25} />
        </mesh>
        <mesh position={[0, 0.55, 0]}>
          <cylinderGeometry args={[0.08, 0.12, 0.5, 12]} />
          <meshStandardMaterial color={ARENA.gold} metalness={0.9} roughness={0.2} />
        </mesh>
        <mesh position={[0, 1.0, 0]}>
          <sphereGeometry args={[0.36, 20, 20]} />
          <meshStandardMaterial color={ARENA.gold} metalness={0.9} roughness={0.15} emissive={ARENA.gold} emissiveIntensity={0.4} />
        </mesh>
      </group>
    </Float>
  );
}

function PodiumSide({ teamKey, players, won }) {
  const side = teamKey === "left" ? -1 : 1;
  const team = teamOf(teamKey);
  const height = won ? 1.7 : 0.8;
  return (
    <group position={[side * 3.6, 0, 0]}>
      <mesh position={[0, height / 2, 0]}>
        <cylinderGeometry args={[2.6, 2.9, height, 36]} />
        <meshStandardMaterial color={team.dark} metalness={0.5} roughness={0.45} />
      </mesh>
      <mesh position={[0, height + 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[2.6, 0.06, 8, 48]} />
        <meshStandardMaterial color={team.color} emissive={team.color} emissiveIntensity={1.5} toneMapped={false} />
      </mesh>
      {players.map((p, i) => (
        <Avatar
          key={p.id || i}
          player={p}
          position={[(i % 3) * 1.5 - 1.5, height, Math.floor(i / 3) * 1.4 - 0.6]}
          mood={won ? "celebrate" : "sad"}
          scale={0.8}
        />
      ))}
      {won && <Trophy position={[0, height + 0.1, 1.8]} />}
    </group>
  );
}

const ResultsView = ({ result, players, myTeam, isAdmin, onClose }) => {
  const navigate = useNavigate();
  const { resetGame } = useGame();
  const played = useRef(false);

  const isTie = result?.winnerTeam === "tie";
  const winner = result?.winnerTeam === "right" ? "right" : "left";
  const iWon = result?.winnerTeam ? result.winnerTeam === myTeam : false;
  const winTeam = teamOf(winner);

  const leftTeam = players.filter((p) => p.team === "left");
  const rightTeam = players.filter((p) => p.team === "right");

  useEffect(() => {
    if (!played.current) {
      played.current = true;
      iWon || isTie ? sfx.fanfare() : sfx.sad();
    }
  }, [iWon, isTie]);

  useGameEvent("roomClosed", () => {
    toast.info("Room closed", infoToastStyle);
    setTimeout(() => {
      resetGame();
      navigate("/");
    }, 800);
  });

  const handleClose = () => {
    onClose && onClose();
    if (!isAdmin) {
      resetGame();
      navigate("/");
    }
  };

  return (
    <>
      <ArenaCanvas
        cameraBase={[0, 3.4, 10.5]}
        cameraLook={[0, 1.6, 0]}
        sway={1}
        ringColor={isTie ? ARENA.gold : winTeam.color}
      >
        <ConfettiRain active />
        <PodiumSide teamKey="left" players={leftTeam} won={isTie || winner === "left"} />
        <PodiumSide teamKey="right" players={rightTeam} won={isTie || winner === "right"} />
      </ArenaCanvas>

      <div className="result-banner">
        <p
          className={`win-team display ${
            isTie ? "gold" : winner === "left" ? "lime" : "violet"
          }`}
        >
          {isTie ? "IT'S A TIE!" : `${winTeam.name.toUpperCase()} WIN!`}
        </p>
        <div className="final-score">
          {result?.left_team_points ?? 0}
          <span className="dash"> — </span>
          {result?.right_team_points ?? 0}
        </div>
      </div>

      <div className="hud-top">
        <div className="chip">
          {isTie
            ? "🤝 DEAD EVEN — REMATCH REQUIRED!"
            : iWon
            ? "🏆 YOUR TEAM TOOK THE CROWN!"
            : "💪 SO CLOSE — REMATCH TIME?"}
        </div>
        <button className="abtn abtn-gold" style={{ margin: 0 }} onClick={handleClose}>
          {isAdmin ? "CLOSE ROOM" : "EXIT"}
        </button>
      </div>

      <ToastContainer />
    </>
  );
};

export default ResultsView;
